import { apiLoadChatContext, apiUpsertChatContext } from '../../api/chatGoalState';
import { apiGetNextRequestKeyForDoc, apiInsertChatRequest, apiInsertChatRequestAttempt } from '../../api/chatRequests';
import { apiLoadChunks, apiLoadChunksByHashes, apiSaveChunkSummariesByHash } from '../../api/chunks';
import { apiLoadDocContext, apiLoadProjectContext, apiUpsertDocContext, apiUpsertProjectContext } from '../../api/docContext';
import { apiAiChatCompletions } from '../../api/ai';
import { apiGetProject } from '../../api/projects';
import CONFIG from '../../config';
import { debug } from '../debug';
import { formatAiResponseText } from '../formatHelpers';
import {
  classifyChatIntentHeuristics,
  classifyChatIntentWithModelFallback,
  classifyChatIntentAndContractWithModelFallback,
  normalizeIntentKey
} from './aiIntentHelpers';
import { executeIntentAndGoalUpdateFlow } from './stages/intent';
import { buildHybridQueryPlan } from './aiQueryHelpers';
import {
  executePlanAndEmitStage,
  executeRetrieveSufficiencyFlow,
  evaluateContextSufficiency as evaluateContextSufficiencyStage,
  buildRefineQueryPlan as buildRefineQueryPlanStage
} from './stages/retrieve/index';
import {
  buildContextRetrievalPipeline as buildContextRetrievalPipelineStage,
  normalizeContextText
} from './stages/retrieve/pipeline';
import {
  summarizeContextReferences,
  summarizeSourceBreakdown,
  summarizeDocSelectionBreakdown,
  clipAuditText,
  summarizeContextItemsForAudit,
  buildContextKeywordCoverage,
  analyzeImplicitTargetPrompt
} from './stages/retrieve/audit';
import { createStageEmitter } from './stages/trace';
import {
  executeConsolidateStage,
  executeConsolidationStage,
  executeSelectAndEmitStage,
  buildFallbackMessages,
  executeRequestStage,
  executeRequestWithCandidateRetries,
  executeGroundingStage,
  collectGroundingEvidenceAnchors,
  validateGroundingAnchors,
  buildGroundingRetryMessage,
  buildGroundingUncertaintyText,
  executeFinalizeStage,
  createFinalizeStagePayload,
  createAssistantTrace,
  shouldRetryRequestError,
  isGuardrailRestrictionError,
  isModelUnavailableError,
  isTransientRateLimitError,
  createRequestRollupArtifacts,
  createRequestLogAuditRecord,
  createPendingRequestLogPayload,
  executePendingRequestLogStage,
  createRequestCandidates,
  executeRequestWithFallbackMessages,
  executeRequestLogPersistStage,
  extractUsageMetrics,
  calculateUsageCostUsd,
  sleep,
  buildRateLimitError
} from './stages/respond';
import {
  executeHydrateAndEmitStage,
  executePersistAndEmitStage,
  createPersistStateRecord
} from './stages/persist';
import { executeRerankerStage } from './stages/router';
import { shouldRunReranker } from './stages/router/gating';
import {
  mergeGoalState,
  normalizeGoalState,
  summarizeGoalStateForAudit
} from './aiGoalStateHelpers';
import {
  extractAssistantTextFromOpenRouter
} from './aiResponseHelpers';
import { getDefaultModelForTask } from './aiConfigRuntimeHelpers';

const unavailableModelIds = new Set();
const DEFAULT_BLOCKED_MODELS = ['stepfun/step-3.5-flash:free'];
const MAX_TRANSIENT_RETRIES_PER_CANDIDATE = 2;
const BASE_TRANSIENT_RETRY_DELAY_MS = 800;
const OPENROUTER_RATE_LIMIT_COOLDOWN_MS = 45000;
const DEFAULT_SINGLE_DOC_ACTIVE_DOC_BOOST = 1.6;
const CONTEXT_SUFFICIENCY_MIN_ITEMS = 3;
const CONTEXT_SUFFICIENCY_MIN_CHARS = 700;
const CONTEXT_SUFFICIENCY_MIN_COVERAGE_RATIO = 0.34;
const CONTEXT_SUFFICIENCY_MIN_MATCHED_KEYWORDS = 2;
const SUMMARY_MAX_DOCS_PER_TURN = 3;
const SUMMARY_MAX_CHUNKS_PER_DOC = 6;
const SUMMARY_MAX_CHUNK_CHARS = 1300;
const SUMMARY_MODEL_TIMEOUT_MS = 12000;
const PROJECT_SUMMARY_MAX_CHARS = 5000;
let openRouterRateLimitUntilMs = 0;
const requestKeyCountersByDoc = new Map();

const FAST_REPHRASE_VERBS_RE = /\b(rewrite|rephrase|reword|polish|tighten|shorten|improve|edit|fix)\b/i;
const FAST_REPHRASE_EXPANSION_RE = /\b(add|append|expand|extend|elaborate|include|introduce|invent|new info|new information|extra context|example|examples|research|fact check|facts?|citations?|source|sources)\b/i;

const isSelectionScopedRewritePrompt = ({ mode = '', intentKey = '', userText = '' } = {}) => {
  if (String(mode || '').trim() !== 'Write') return false;
  if (normalizeIntentKey(intentKey) !== 'edit') return false;

  const normalizedUserText = String(userText || '').trim();
  if (!normalizedUserText || normalizedUserText.length > 180) return false;
  if (!FAST_REPHRASE_VERBS_RE.test(normalizedUserText)) return false;
  if (FAST_REPHRASE_EXPANSION_RE.test(normalizedUserText)) return false;
  return true;
};

const isFastRephraseNoNewInfoRequest = ({ mode = '', intentKey = '', userText = '', hasSelectionContext = false } = {}) => {
  if (!isSelectionScopedRewritePrompt({ mode, intentKey, userText })) return false;
  if (!hasSelectionContext) return false;
  return true;
};

const carryForwardLinkedDocsFromGoalState = ({ contextPlan = null, goalState = null, userText = '' } = {}) => {
  const nextContextPlan = contextPlan && typeof contextPlan === 'object'
    ? contextPlan
    : {};
  const currentLinkedDocIds = Array.isArray(nextContextPlan.linkedDocIds)
    ? nextContextPlan.linkedDocIds.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
    : [];

  if (currentLinkedDocIds.length > 0) return nextContextPlan;

  const persistedLinkedDocIds = Array.isArray(goalState?.targets?.linkedDocIds)
    ? goalState.targets.linkedDocIds.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
    : [];

  if (persistedLinkedDocIds.length === 0) return nextContextPlan;

  const promptSignals = analyzeImplicitTargetPrompt({ userText });
  if (!promptSignals?.shouldAssumeAttachmentTarget) return nextContextPlan;

  return {
    ...nextContextPlan,
    linkedDocIds: Array.from(new Set(persistedLinkedDocIds)),
    includeLinkedDocs: true
  };
};

const tryParseJsonObject = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (error) {
    // ignore and attempt fenced/substring extraction
  }

  const fencedMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fencedMatch?.[1]) {
    try {
      const parsed = JSON.parse(String(fencedMatch[1] || '').trim());
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (error) {
      // ignore and attempt brace extraction
    }
  }

  const firstBrace = raw.indexOf('{');
  const lastBrace = raw.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    try {
      const parsed = JSON.parse(raw.slice(firstBrace, lastBrace + 1));
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (error) {
      return null;
    }
  }

  return null;
};

const createSummaryFingerprint = ({ docId = 0, chunkRows = [] } = {}) => {
  const normalizedDocId = Number(docId || 0);
  const parts = (Array.isArray(chunkRows) ? chunkRows : [])
    .map((row) => {
      const chunkHash = String(row?.chunk_hash || '').trim();
      const updatedAt = String(row?.updated_at || '').trim();
      if (!chunkHash) return null;
      return `${chunkHash}:${updatedAt}`;
    })
    .filter(Boolean)
    .sort();
  return `doc:${normalizedDocId}|chunks:${parts.join('|')}`;
};

const buildProjectSummaryIndex = ({ docSummaries = [] } = {}) => {
  const rows = Array.isArray(docSummaries) ? docSummaries : [];
  const termsCount = new Map();
  const tokenize = (value = '') => {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 4);
  };

  rows.forEach((entry) => {
    tokenize(entry?.summaryMd || '').forEach((token) => {
      termsCount.set(token, Number(termsCount.get(token) || 0) + 1);
    });
  });

  const themes = Array.from(termsCount.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, 24)
    .map(([term, count]) => ({ term, count }));

  const evidenceMap = rows.map((entry) => ({
    docId: Number(entry?.docId || 0),
    terms: tokenize(entry?.summaryMd || '').slice(0, 12)
  })).filter((entry) => entry.docId > 0 && entry.terms.length > 0);

  return {
    themes,
    evidenceMap
  };
};

const upsertProjectSummaryFromSummarizeStage = async ({
  projectId = 0,
  summarizeStage = null
} = {}) => {
  const normalizedProjectId = Number(projectId || 0);
  if (
    normalizedProjectId <= 0
    || summarizeStage?.status !== 'ok'
    || !Array.isArray(summarizeStage?.docSummaries)
    || summarizeStage.docSummaries.length === 0
  ) {
    return;
  }

  const mergedProjectSummary = summarizeStage.docSummaries
    .map((entry) => {
      const docId = Number(entry?.docId || 0);
      const summaryMd = normalizeContextText(entry?.summaryMd || '');
      if (!summaryMd || docId <= 0) return null;
      return `Doc ${docId}: ${summaryMd}`;
    })
    .filter(Boolean)
    .join('\n\n')
    .slice(0, PROJECT_SUMMARY_MAX_CHARS);

  if (!mergedProjectSummary) {
    return;
  }

  const summaryIndex = buildProjectSummaryIndex({ docSummaries: summarizeStage.docSummaries });
  await apiUpsertProjectContext({
    projectId: normalizedProjectId,
    summaryMd: mergedProjectSummary,
    sourceFingerprint: `summarize:${new Date().toISOString()}`,
    summaryState: {
      updatedAt: new Date().toISOString(),
      source: 'summarize_stage',
      summarizedDocIds: Array.isArray(summarizeStage?.summarizedDocIds)
        ? summarizeStage.summarizedDocIds.slice(0, 32)
        : [],
      summaryIndex
    }
  });
};

const buildSummaryCandidateChunkHashes = async ({
  baseChunkHashes = [],
  maxPerTurn = 16
} = {}) => {
  const cappedMax = Math.max(1, Number(maxPerTurn || 16));
  const baseOrdered = Array.from(new Set(
    (Array.isArray(baseChunkHashes) ? baseChunkHashes : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )).slice(0, cappedMax);

  if (baseOrdered.length === 0 || baseOrdered.length >= cappedMax) {
    return baseOrdered;
  }

  const baseRows = await apiLoadChunksByHashes(baseOrdered);
  const normalizedBaseRows = Array.isArray(baseRows)
    ? baseRows.filter((row) => Number(row?.doc_id || 0) > 0 && String(row?.chunk_hash || '').trim())
    : [];
  if (normalizedBaseRows.length === 0) {
    return baseOrdered;
  }

  const docOrder = new Map();
  const baseChunkSetByDoc = new Map();
  normalizedBaseRows.forEach((row) => {
    const docId = Number(row?.doc_id || 0);
    const chunkHash = String(row?.chunk_hash || '').trim();
    const baseIndex = baseOrdered.indexOf(chunkHash);
    const existingOrder = Number(docOrder.get(docId) ?? Number.MAX_SAFE_INTEGER);
    if (baseIndex >= 0 && baseIndex < existingOrder) {
      docOrder.set(docId, baseIndex);
    }
    const setForDoc = baseChunkSetByDoc.get(docId) || new Set();
    setForDoc.add(chunkHash);
    baseChunkSetByDoc.set(docId, setForDoc);
  });

  const docIds = Array.from(baseChunkSetByDoc.keys())
    .sort((left, right) => {
      const leftOrder = Number(docOrder.get(left) ?? Number.MAX_SAFE_INTEGER);
      const rightOrder = Number(docOrder.get(right) ?? Number.MAX_SAFE_INTEGER);
      return leftOrder - rightOrder;
    });

  const partialDocCandidates = [];
  for (const docId of docIds) {
    const [docChunks, existingDocContext] = await Promise.all([
      apiLoadChunks(docId),
      apiLoadDocContext({ docId })
    ]);
    const normalizedDocChunks = Array.isArray(docChunks)
      ? docChunks.filter((row) => String(row?.chunk_hash || '').trim())
      : [];
    if (normalizedDocChunks.length === 0) continue;

    const alreadySummarizedHashes = new Set(
      normalizedDocChunks
        .filter((row) => String(row?.summary_md || '').trim())
        .map((row) => String(row?.chunk_hash || '').trim())
        .filter(Boolean)
    );
    const existingChunkSummaries = Array.isArray(existingDocContext?.chunkSummaries)
      ? existingDocContext.chunkSummaries
      : [];
    existingChunkSummaries.forEach((entry) => {
      const hash = String(entry?.chunkHash || entry?.chunk_hash || '').trim();
      if (hash) alreadySummarizedHashes.add(hash);
    });

    const baseForDoc = baseChunkSetByDoc.get(docId) || new Set();
    const plannedOrSummarized = new Set([...alreadySummarizedHashes, ...baseForDoc]);
    const missingHashes = normalizedDocChunks
      .map((row) => String(row?.chunk_hash || '').trim())
      .filter((hash) => hash && !plannedOrSummarized.has(hash));

    if (alreadySummarizedHashes.size > 0 && missingHashes.length > 0) {
      partialDocCandidates.push({
        docId,
        order: Number(docOrder.get(docId) ?? Number.MAX_SAFE_INTEGER),
        missingHashes
      });
    }
  }

  if (partialDocCandidates.length === 0) {
    return baseOrdered;
  }

  const expansionBudget = cappedMax - baseOrdered.length;
  if (expansionBudget <= 0) {
    return baseOrdered;
  }

  const expandedHashes = [];
  partialDocCandidates
    .sort((left, right) => {
      if (left.missingHashes.length !== right.missingHashes.length) {
        return left.missingHashes.length - right.missingHashes.length;
      }
      return left.order - right.order;
    })
    .forEach((candidate) => {
      for (const hash of candidate.missingHashes) {
        if (expandedHashes.length >= expansionBudget) break;
        expandedHashes.push(hash);
      }
    });

  return Array.from(new Set([
    ...baseOrdered,
    ...expandedHashes
  ])).slice(0, cappedMax);
};

const runSummarizeStage = async ({
  contextRetrieval = null,
  userText = '',
  signal,
  onModelAttempt,
  persistWrites = true
} = {}) => {
  const empty = {
    status: 'skipped',
    decisionCode: 'summarize_no_candidates',
    reasonCodes: ['no_summary_candidates'],
    candidateChunkCount: 0,
    summarizedChunkCount: 0,
    summarizedDocCount: 0,
    summarizedChunkHashes: [],
    summarizedDocIds: [],
    errors: [],
    chunkRelevanceMap: new Map()
  };

  const candidateChunkHashes = Array.isArray(contextRetrieval?.meta?.summaryBackfillNeededChunkHashes)
    ? contextRetrieval.meta.summaryBackfillNeededChunkHashes.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  if (candidateChunkHashes.length === 0) return empty;

  const chunkRows = await apiLoadChunksByHashes(candidateChunkHashes);
  const candidateOrder = new Map();
  candidateChunkHashes.forEach((chunkHash, index) => {
    if (!candidateOrder.has(chunkHash)) {
      candidateOrder.set(chunkHash, index);
    }
  });
  const rows = Array.isArray(chunkRows)
    ? chunkRows
      .filter((row) => {
        const chunkHash = String(row?.chunk_hash || '').trim();
        const docId = Number(row?.doc_id || 0);
        return Boolean(chunkHash) && docId > 0;
      })
      .sort((left, right) => {
        const leftOrder = Number(candidateOrder.get(String(left?.chunk_hash || '').trim()) ?? Number.MAX_SAFE_INTEGER);
        const rightOrder = Number(candidateOrder.get(String(right?.chunk_hash || '').trim()) ?? Number.MAX_SAFE_INTEGER);
        return leftOrder - rightOrder;
      })
    : [];

  if (rows.length === 0) {
    return {
      ...empty,
      decisionCode: 'summarize_chunks_unavailable',
      reasonCodes: ['summary_chunks_unavailable'],
      candidateChunkCount: candidateChunkHashes.length
    };
  }

  const byDoc = new Map();
  const docFirstChunkOrder = new Map();
  for (const row of rows) {
    const docId = Number(row?.doc_id || 0);
    const bucket = byDoc.get(docId) || [];
    bucket.push(row);
    byDoc.set(docId, bucket);
    const rowOrder = Number(candidateOrder.get(String(row?.chunk_hash || '').trim()) ?? Number.MAX_SAFE_INTEGER);
    const prevDocOrder = Number(docFirstChunkOrder.get(docId) ?? Number.MAX_SAFE_INTEGER);
    if (rowOrder < prevDocOrder) {
      docFirstChunkOrder.set(docId, rowOrder);
    }
  }

  const modelId = getDefaultModelForTask('summary.auto.chunk', 'extract')
    || getDefaultModelForTask('summary.auto.doc', 'extract')
    || getDefaultModelForTask('chat.explore', 'response');
  const summaryModelTimeoutMs = Math.max(
    1000,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.SUMMARY_MODEL_TIMEOUT_MS || SUMMARY_MODEL_TIMEOUT_MS)
  );
  const docEntries = Array.from(byDoc.entries())
    .sort((left, right) => {
      const leftOrder = Number(docFirstChunkOrder.get(Number(left?.[0] || 0)) ?? Number.MAX_SAFE_INTEGER);
      const rightOrder = Number(docFirstChunkOrder.get(Number(right?.[0] || 0)) ?? Number.MAX_SAFE_INTEGER);
      return leftOrder - rightOrder;
    })
    .slice(0, SUMMARY_MAX_DOCS_PER_TURN);
  const allSavedChunkSummaries = [];
  const summarizedDocIds = [];
  const summarizedChunkHashes = [];
  const docSummaries = [];
  const errors = [];
  const chunkRelevanceMap = new Map();

  for (const [docId, docRowsRaw] of docEntries) {
    const docRows = (Array.isArray(docRowsRaw) ? docRowsRaw : [])
      .sort((left, right) => {
        const leftOrder = Number(candidateOrder.get(String(left?.chunk_hash || '').trim()) ?? Number.MAX_SAFE_INTEGER);
        const rightOrder = Number(candidateOrder.get(String(right?.chunk_hash || '').trim()) ?? Number.MAX_SAFE_INTEGER);
        return leftOrder - rightOrder;
      })
      .slice(0, SUMMARY_MAX_CHUNKS_PER_DOC);
    if (docRows.length === 0) continue;

    const existingContext = await apiLoadDocContext({ docId: Number(docId) });
    const summaryPayload = docRows.map((row) => {
      const chunkHash = String(row?.chunk_hash || '').trim();
      const markdown = String(row?.content_md || '').trim();
      const truncatedMd = markdown.length > SUMMARY_MAX_CHUNK_CHARS
        ? `${markdown.slice(0, SUMMARY_MAX_CHUNK_CHARS)}...`
        : markdown;
      return {
        chunk_hash: chunkHash,
        content_md: truncatedMd,
        char_length: Number(row?.char_length || markdown.length || 0) || 0
      };
    }).filter((entry) => entry.chunk_hash && entry.content_md);

    if (summaryPayload.length === 0) continue;

    const systemMessage = 'You summarize document chunks for retrieval. Return STRICT JSON only.';
    const userMessage = [
      'Summarize the provided chunks for retrieval reuse.',
      'Keep summaries concise, factual, and tied to the user question context when relevant.',
      `User question: ${String(userText || '').trim() || 'n/a'}`,
      'Return JSON with this exact shape:',
      '{"doc_summary_md":"...","chunk_summaries":[{"chunk_hash":"...","summary_md":"...","relevance":"keep|dump","confidence":0.0}]}.',
      `Document id: ${Number(docId)}`,
      `Chunks: ${JSON.stringify(summaryPayload)}`
    ].join('\n');

    const attemptStart = Date.now();
    try {
      const timeoutController = (typeof AbortController === 'function') ? new AbortController() : null;
      const summarizeSignal = timeoutController ? timeoutController.signal : signal;
      let timeoutId = null;
      let parentAbortHandler = null;
      let response = null;
      if (timeoutController) {
        if (signal?.aborted) {
          timeoutController.abort(signal.reason);
        } else if (signal && typeof signal.addEventListener === 'function') {
          parentAbortHandler = () => timeoutController.abort(signal.reason);
          signal.addEventListener('abort', parentAbortHandler, { once: true });
        }
        timeoutId = setTimeout(() => {
          timeoutController.abort(new Error('summarize_timeout'));
        }, summaryModelTimeoutMs);
      }

      try {
        response = await apiAiChatCompletions({
          model: modelId,
          messages: [
            { role: 'system', content: systemMessage },
            { role: 'user', content: userMessage }
          ],
          temperature: 0.1,
          max_tokens: 1200,
          signal: summarizeSignal
        });
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (signal && parentAbortHandler && typeof signal.removeEventListener === 'function') {
          signal.removeEventListener('abort', parentAbortHandler);
        }
      }

      if (typeof onModelAttempt === 'function') {
        const usagePayload = response?.usage && typeof response.usage === 'object'
          ? {
              promptTokens: Number(response.usage.prompt_tokens ?? response.usage.promptTokens ?? 0) || 0,
              completionTokens: Number(response.usage.completion_tokens ?? response.usage.completionTokens ?? 0) || 0,
              totalTokens: Number(response.usage.total_tokens ?? response.usage.totalTokens ?? 0) || 0
            }
          : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
        onModelAttempt({
          stageName: 'summarize',
          callKind: 'summarize',
          modelId,
          responseModel: String(response?.model || modelId || '').trim() || modelId,
          providerRequestId: String(response?.id || '').trim() || null,
          usage: usagePayload,
          status: 'success',
          durationMs: Date.now() - attemptStart,
          errorCode: null,
          errorMessage: null
        });
      }

      const responseText = extractAssistantTextFromOpenRouter(response);
      const parsed = tryParseJsonObject(responseText);
      if (!parsed || typeof parsed !== 'object') {
        errors.push({ docId, message: 'summarize_parse_failed' });
        continue;
      }

      const parsedChunkSummaries = Array.isArray(parsed?.chunk_summaries)
        ? parsed.chunk_summaries
            .map((entry) => {
              const chunkHash = String(entry?.chunk_hash || '').trim();
              const summaryMd = normalizeContextText(entry?.summary_md || '');
              if (!chunkHash || !summaryMd) return null;
              return {
                chunkHash,
                summaryMd,
                relevance: String(entry?.relevance || 'keep').trim().toLowerCase() === 'dump' ? 'dump' : 'keep',
                confidence: Number.isFinite(Number(entry?.confidence))
                  ? Math.max(0, Math.min(1, Number(entry.confidence)))
                  : null,
                summarizedAt: new Date().toISOString()
              };
            })
            .filter(Boolean)
        : [];

      if (parsedChunkSummaries.length === 0) {
        errors.push({ docId, message: 'summarize_empty_chunk_summaries' });
        continue;
      }

      if (persistWrites) {
        await apiSaveChunkSummariesByHash(parsedChunkSummaries.map((entry) => ({
          chunk_hash: entry.chunkHash,
          summary_md: entry.summaryMd,
          summarized_at: entry.summarizedAt
        })));
      }

      const docSummaryMd = normalizeContextText(parsed?.doc_summary_md || existingContext?.docSummaryMd || '');
      const summaryFingerprint = createSummaryFingerprint({ docId, chunkRows: docRows });
      const summarizedChunkHashesForDoc = parsedChunkSummaries.map((entry) => entry.chunkHash).filter(Boolean);
      if (docSummaryMd) {
        docSummaries.push({ docId: Number(docId), summaryMd: docSummaryMd });
      }

      if (persistWrites) {
        await apiUpsertDocContext({
          docId,
          docSummaryMd: docSummaryMd || null,
          sourceFingerprint: summaryFingerprint,
          chunkSummaries: parsedChunkSummaries.map((entry) => ({
            chunkHash: entry.chunkHash,
            relevance: entry.relevance,
            confidence: entry.confidence ?? 1
          })),
          summaryState: {
            ...(existingContext?.summaryState && typeof existingContext.summaryState === 'object' ? existingContext.summaryState : {}),
            summarizeStage: {
              updatedAt: new Date().toISOString(),
              summarizedChunkCount: parsedChunkSummaries.length,
              candidateChunkCount: summaryPayload.length,
              model: modelId,
              summarizedChunkHashes: summarizedChunkHashesForDoc.slice(0, 64)
            }
          }
        });
      }

      summarizedDocIds.push(docId);
      parsedChunkSummaries.forEach((entry) => {
        summarizedChunkHashes.push(entry.chunkHash);
        chunkRelevanceMap.set(entry.chunkHash, { relevance: entry.relevance, confidence: entry.confidence ?? 1 });
      });
      allSavedChunkSummaries.push(...parsedChunkSummaries);
    } catch (error) {
      const message = String(error?.message || 'summarize_failed');
      const normalized = message.toLowerCase();
      const isTimeoutLike = normalized.includes('summarize_timeout')
        || normalized.includes('timed out')
        || normalized.includes('cancelled before completion');
      if (typeof onModelAttempt === 'function') {
        onModelAttempt({
          stageName: 'summarize',
          callKind: 'summarize',
          modelId,
          responseModel: null,
          providerRequestId: null,
          usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
          status: isTimeoutLike ? 'timeout' : 'failed',
          durationMs: Date.now() - attemptStart,
          errorCode: isTimeoutLike ? 'REQUEST_TIMEOUT' : (String(error?.code || '').trim() || null),
          errorMessage: isTimeoutLike ? 'summarize_timeout' : message
        });
      }
      errors.push({ docId, message: isTimeoutLike ? 'summarize_timeout' : message });
    }
  }

  if (allSavedChunkSummaries.length === 0) {
    return {
      status: errors.length > 0 ? 'failed' : 'skipped',
      decisionCode: errors.length > 0 ? 'summarize_failed' : 'summarize_no_valid_chunks',
      reasonCodes: errors.length > 0 ? ['summarize_error'] : ['no_valid_summary_chunks'],
      candidateChunkCount: candidateChunkHashes.length,
      summarizedChunkCount: 0,
      summarizedDocCount: 0,
      summarizedChunkHashes: [],
      summarizedDocIds: [],
      errors,
      chunkRelevanceMap: new Map(),
      docSummaries: []
    };
  }

  return {
    status: 'ok',
    decisionCode: 'summarize_completed',
    reasonCodes: [],
    candidateChunkCount: candidateChunkHashes.length,
    summarizedChunkCount: allSavedChunkSummaries.length,
    summarizedDocCount: summarizedDocIds.length,
    summarizedChunkHashes: Array.from(new Set(summarizedChunkHashes)),
    summarizedDocIds: Array.from(new Set(summarizedDocIds)),
    errors,
    chunkRelevanceMap,
    docSummaries
  };
};

const buildIntentContextBundle = async ({
  chatTurn = null,
  goalState = null,
  activeDocId = 0,
  projectId = 0,
  linkedDocIds = [],
  includeProjectSummary = true,
  includeLinkedDocSummaries = true,
  maxLinkedDocSummaries = 6
} = {}) => {
  const bundle = {
    activeDocId: Number(activeDocId || 0) || null,
    projectId: Number(projectId || 0) || null,
    linkedDocIds: Array.isArray(linkedDocIds) ? linkedDocIds.slice(0, 16) : [],
    mode: String(chatTurn?.mode || '').trim() || null,
    goalStateSummary: String(goalState?.summary || '').trim() || null,
    goalStateKeywords: Array.isArray(goalState?.keywords) ? goalState.keywords.slice(0, 12) : [],
    activeDocSummary: null,
    projectSummary: null,
    linkedDocSummaries: []
  };

  if (bundle.activeDocId && bundle.activeDocId > 0) {
    try {
      const activeDocContext = await apiLoadDocContext({ docId: bundle.activeDocId });
      const summary = normalizeContextText(activeDocContext?.docSummaryMd || '');
      if (summary) bundle.activeDocSummary = summary;
    } catch {
      // Best-effort context enrichment only.
    }
  }

  if (includeProjectSummary && bundle.projectId && bundle.projectId > 0) {
    try {
      const projectContext = await apiLoadProjectContext({ projectId: bundle.projectId });
      const summary = normalizeContextText(projectContext?.summaryMd || '');
      if (summary) bundle.projectSummary = summary;
    } catch {
      // Best-effort context enrichment only.
    }
  }

  if (includeLinkedDocSummaries && Array.isArray(bundle.linkedDocIds) && bundle.linkedDocIds.length > 0) {
    const cappedLinkedDocIds = bundle.linkedDocIds
      .slice(0, Math.max(0, Number(maxLinkedDocSummaries || 6)));
    const linkedContextRows = await Promise.all(cappedLinkedDocIds.map(async (docId) => {
      try {
        const row = await apiLoadDocContext({ docId: Number(docId || 0) });
        const summary = normalizeContextText(row?.docSummaryMd || '');
        if (!summary) return null;
        return {
          docId: Number(docId || 0),
          summary
        };
      } catch {
        return null;
      }
    }));

    bundle.linkedDocSummaries = linkedContextRows.filter(Boolean);
  }

  return bundle;
};

const createRequestId = (docId = null) => {
  const normalizedDocId = Number(docId || 0);
  const prefix = Number.isFinite(normalizedDocId) && normalizedDocId > 0 ? String(normalizedDocId) : 'chat';
  const current = Number(requestKeyCountersByDoc.get(prefix) || 0);
  const next = current + 1;
  requestKeyCountersByDoc.set(prefix, next);
  return `${prefix}_${next}`;
};

const normalizeBlockedModelIds = (values = []) => {
  return Array.from(new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean)
  ));
};

const buildBlockedModelSet = () => {
  const configured = normalizeBlockedModelIds(CONFIG.AI?.CHAT_ORCHESTRATION?.BLOCKED_MODELS);
  const defaults = normalizeBlockedModelIds(DEFAULT_BLOCKED_MODELS);
  return new Set([...(configured.length ? configured : defaults)]);
};

const normalizeResponseModelId = (value = '') => String(value || '').trim().toLowerCase();

const isBlockedResponseModel = (responseModel = '', blockedModelSet = new Set()) => {
  const normalized = normalizeResponseModelId(responseModel);
  if (!normalized) return false;
  return blockedModelSet.has(normalized);
};


// Re-exports sufficiency evaluation from the retrieve stage module.
export const evaluateContextSufficiency = evaluateContextSufficiencyStage;

// Re-exports refine planning from the retrieve stage module.
export const buildRefineQueryPlan = buildRefineQueryPlanStage;

export const buildContextRetrievalPipeline = buildContextRetrievalPipelineStage;

export const buildChatTurnContract = ({
  docJson,
  userText,
  orchestration = null,
  systemPrompt,
  maxHistoryMessages = 20,
  intentModel,
  model,
  fallbackModels = [],
  temperature,
  maxTokens,
  stream = false,
  signal
} = {}) => {
  const featureFlags = CONFIG.AI?.CHAT_ORCHESTRATION || {};
  const isEnabled = Boolean(featureFlags.ENABLED);
  const legacyFallback = featureFlags.LEGACY_FALLBACK === undefined
    ? true
    : Boolean(featureFlags.LEGACY_FALLBACK);
  const requestedMode = String(orchestration?.mode || 'Explore').trim().toLowerCase();
  const mode = requestedMode === 'write' || requestedMode === 'edit' ? 'Write' : 'Explore';
  const heuristicIntent = classifyChatIntentHeuristics({ userText, mode });
  const configuredSingleDocBoost = Number(featureFlags.SINGLE_DOC_ACTIVE_DOC_BOOST);
  const singleDocActiveDocBoost = Number.isFinite(configuredSingleDocBoost) && configuredSingleDocBoost > 0
    ? configuredSingleDocBoost
    : DEFAULT_SINGLE_DOC_ACTIVE_DOC_BOOST;
  const singleDocStrictActiveDoc = Boolean(featureFlags.SINGLE_DOC_STRICT_ACTIVE_DOC);
  const recencyWeight = Number(CONFIG.AI?.CHAT_ORCHESTRATION?.RECENCY_WEIGHT || 0.35);
  const recencyDecayTurns = Number(CONFIG.AI?.CHAT_ORCHESTRATION?.RECENCY_DECAY_TURNS || 8);
  const projectId = Number(orchestration?.projectId || 0) || null;
  const activeDocId = Number(orchestration?.activeDocId || 0) || null;
  const activeDocType = String(orchestration?.activeDocType || '').trim().toLowerCase() || null;
  const initiatorUserId = String(orchestration?.initiatorUserId || orchestration?.userId || '').trim() || null;
  const billingOwnerUserId = String(orchestration?.billingOwnerUserId || orchestration?.ownerUserId || '').trim() || null;
  const linkedDocIds = Array.isArray(orchestration?.linkedDocIds)
    ? Array.from(new Set(
      orchestration.linkedDocIds
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)
    ))
    : [];
  const selectionContexts = Array.isArray(orchestration?.selectionContexts)
    ? orchestration.selectionContexts
      .map((entry) => {
        const docId = Number(entry?.docId || 0);
        if (!Number.isFinite(docId) || docId <= 0) return null;
        const selectedText = String(entry?.selectedText || '').trim();
        return {
          docId,
          key: String(entry?.key || '').trim() || null,
          selectedText: selectedText || null
        };
      })
      .filter(Boolean)
    : [];
  const hasSelectionContext = selectionContexts.some((entry) => {
    return Number(entry?.docId || 0) > 0 && String(entry?.selectedText || '').trim().length > 0;
  });
  const normalizedUserText = String(userText || '').trim();
  const isTransactionalSelectionEdit = mode === 'Write'
    && normalizeIntentKey(heuristicIntent?.key) === 'edit'
    && hasSelectionContext
    && /\b(rewrite|rephrase|reword|polish|tighten|shorten|improve|fix|edit)\b/i.test(normalizedUserText)
    && normalizedUserText.length <= 140;
  const selectionScopedRewritePrompt = isSelectionScopedRewritePrompt({
    mode,
    intentKey: heuristicIntent?.key,
    userText: normalizedUserText
  });
  const fastRephraseNoNewInfo = isFastRephraseNoNewInfoRequest({
    mode,
    intentKey: heuristicIntent?.key,
    userText: normalizedUserText,
    hasSelectionContext
  });
  const explicitResponseModelRequested = (() => {
    const normalized = String(orchestration?.model || '').trim().toLowerCase();
    return Boolean(normalized && normalized !== 'default');
  })();
  const templateDocId = Number(orchestration?.templateDocId || 0);
  const hasLinkedDocs = linkedDocIds.length > 0 || (Number.isFinite(templateDocId) && templateDocId > 0);
  const preferLinkedOverChatContext = activeDocType === 'chat' && hasLinkedDocs;
  const includeActiveDoc = orchestration?.includeActiveDoc === undefined
    ? (selectionScopedRewritePrompt ? false : !preferLinkedOverChatContext)
    : Boolean(orchestration.includeActiveDoc);
  const includeLinkedDocs = orchestration?.includeLinkedDocs === undefined
    ? (!isTransactionalSelectionEdit && !selectionScopedRewritePrompt)
    : Boolean(orchestration.includeLinkedDocs);
  const includeChatHistory = orchestration?.includeChatHistory === undefined
    ? ((isTransactionalSelectionEdit || selectionScopedRewritePrompt) ? false : !preferLinkedOverChatContext)
    : Boolean(orchestration.includeChatHistory);
  const includeProjectSearch = orchestration?.includeProjectSearch === undefined
    ? (!isTransactionalSelectionEdit && !selectionScopedRewritePrompt)
    : Boolean(orchestration.includeProjectSearch);
  const queryPlan = buildHybridQueryPlan({
    userText,
    intent: heuristicIntent,
    contextPlan: {
      activeDocId,
      linkedDocIds
    }
  });

  return {
    requestId: String(orchestration?.requestKey || orchestration?.requestId || '').trim() || createRequestId(activeDocId),
    createdAt: Date.now(),
    mode,
    intentModel: String(intentModel || '').trim() || null,
    model: String(model || '').trim() || null,
    fallbackModels: Array.isArray(fallbackModels) ? fallbackModels : [],
    intent: {
      ...heuristicIntent
    },
    queryPlan,
    contextPlan: {
      source: 'phase-c-context-plan',
      projectId,
      activeDocId,
      activeDocType,
      linkedDocIds,
      selectionContexts,
      templateDocId: Number.isFinite(templateDocId) && templateDocId > 0 ? templateDocId : null,
      includeActiveDoc,
      includeLinkedDocs,
      includeChatHistory,
      includeProjectSearch,
      isTransactionalSelectionEdit,
      selectionScopedRewritePrompt,
      fastRephraseNoNewInfo,
      explicitResponseModelRequested,
      preferLinkedOverChatContext,
      singleDocActiveDocBoost,
      singleDocStrictActiveDoc,
      recencyWeight,
      recencyDecayTurns
    },
    actor: {
      initiatorUserId,
      billingOwnerUserId
    },
    formatPolicy: {
      preserveMarkdown: true,
      collapseExtraWhitespace: true
    },
    runtime: {
      enabled: isEnabled,
      legacyFallback,
      routingMode: String(featureFlags.ROUTING_MODE || 'hybrid').trim().toLowerCase() || 'hybrid'
    },
    goalState: null,
    goalStateHydrated: false,
    request: {
      docJson,
      userText,
      systemPrompt,
      maxHistoryMessages,
      temperature,
      maxTokens,
      stream,
      signal
    }
  };
};

export const generateAssistantResponse = async ({
  docJson,
  userText,
  orchestration,
  intentModel,
  model,
  fallbackModels = [],
  systemPrompt,
  maxHistoryMessages = 20,
  temperature,
  maxTokens,
  stream = false,
  onStreamText,
  onStage,
  signal
} = {}) => {
  if (Date.now() < openRouterRateLimitUntilMs) {
    throw buildRateLimitError(openRouterRateLimitUntilMs - Date.now());
  }

  const chatTurn = buildChatTurnContract({
    docJson,
    userText,
    orchestration,
    systemPrompt,
    maxHistoryMessages,
    intentModel,
    model,
    fallbackModels,
    temperature,
    maxTokens,
    stream,
    signal
  });
  const providedRequestKey = String(orchestration?.requestKey || orchestration?.requestId || '').trim();
  if (!providedRequestKey) {
    const activeDocId = Number(chatTurn.contextPlan?.activeDocId || 0);
    try {
      const nextRequestKey = await apiGetNextRequestKeyForDoc({ docId: activeDocId });
      chatTurn.requestId = String(nextRequestKey || '').trim() || createRequestId(activeDocId);
    } catch {
      chatTurn.requestId = createRequestId(activeDocId);
    }
  }
  const stageTrace = [];
  const emitStage = createStageEmitter({ chatTurn, onStage, collector: stageTrace });
  let usedLegacyRequest = false;
  const loggingEnabled = Boolean(CONFIG.AI?.CHAT_ORCHESTRATION?.LOGGING_ENABLED);
  const logAttemptsEnabled = Boolean(CONFIG.AI?.CHAT_ORCHESTRATION?.LOG_ATTEMPTS_ENABLED);
  const logPayloadsEnabled = Boolean(CONFIG.AI?.CHAT_ORCHESTRATION?.LOG_PAYLOADS_ENABLED);
  const payloadSampleRate = Number.isFinite(Number(CONFIG.AI?.CHAT_ORCHESTRATION?.LOG_PAYLOAD_SAMPLE_RATE))
    ? Math.max(0, Math.min(1, Number(CONFIG.AI.CHAT_ORCHESTRATION.LOG_PAYLOAD_SAMPLE_RATE)))
    : 0;
  const maxExcerptChars = Math.max(120, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.LOG_MAX_EXCERPT_CHARS || 700));
  const attemptsIndexEnabled = Boolean(CONFIG.AI?.CHAT_ORCHESTRATION?.LOG_ATTEMPTS_INDEX_ENABLED);
  const shouldCapturePayloads = logPayloadsEnabled
    && (payloadSampleRate >= 1 || (payloadSampleRate > 0 && Math.random() < payloadSampleRate));
  let requestAttemptCounter = 0;
  const requestAttemptsAudit = [];
  const canPersistGoalState = Boolean(chatTurn.runtime.enabled)
    && String(chatTurn.contextPlan?.activeDocType || '').trim().toLowerCase() === 'chat'
    && Number(chatTurn.contextPlan?.activeDocId || 0) > 0;
  const canPersistRequestLogs = canPersistGoalState && loggingEnabled;
  const activeProjectId = Number(chatTurn.contextPlan?.projectId || 0) || null;
  let billingOwnerUserId = String(chatTurn.actor?.billingOwnerUserId || '').trim() || null;
  const initiatorUserId = String(chatTurn.actor?.initiatorUserId || '').trim() || null;
  if (!billingOwnerUserId && Number(activeProjectId || 0) > 0) {
    try {
      const project = await apiGetProject(activeProjectId);
      billingOwnerUserId = String(project?.owner_user_id || '').trim() || null;
    } catch {
      billingOwnerUserId = null;
    }
  }
  let requestLogAudit = createRequestLogAuditRecord({
    enabled: canPersistRequestLogs,
    status: canPersistRequestLogs ? 'pending' : 'skipped',
    id: null,
    error: null
  });
  const logAuxModelAttempt = async ({
    stageName = 'orchestration',
    callKind = 'subtask',
    modelId = null,
    responseModel = null,
    providerRequestId = null,
    usage = null,
    status = 'success',
    durationMs = 0,
    errorCode = null,
    errorMessage = null
  } = {}) => {
    const attemptIndex = requestAttemptCounter + 1;
    requestAttemptCounter = attemptIndex;
    const attemptKey = `${chatTurn.requestId}_${attemptIndex}`;
    const normalizedUsage = usage && typeof usage === 'object'
      ? {
          promptTokens: Number(usage.promptTokens || 0) || 0,
          completionTokens: Number(usage.completionTokens || 0) || 0,
          totalTokens: Number(usage.totalTokens || 0) || 0
        }
      : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
    const resolvedModelId = String(responseModel || modelId || '').trim() || null;
    const resolvedCost = calculateUsageCostUsd({ modelId: resolvedModelId, usage: normalizedUsage }) || {
      promptCostUsd: 0,
      completionCostUsd: 0,
      totalCostUsd: 0
    };
    const normalizedStatus = String(status || 'success').trim().toLowerCase() || 'success';

    requestAttemptsAudit.push({
      attemptIndex,
      attemptKey,
      stageName: String(stageName || 'orchestration').trim() || 'orchestration',
      callKind: String(callKind || 'subtask').trim() || 'subtask',
      providerName: 'openrouter',
      providerRequestId: String(providerRequestId || '').trim() || null,
      candidateModel: String(modelId || '').trim() || null,
      responseModel: resolvedModelId,
      transport: 'single',
      status: normalizedStatus,
      isTimeout: normalizedStatus === 'timeout',
      durationMs: Number(durationMs || 0) || 0,
      usage: normalizedUsage,
      cost: {
        promptCostUsd: Number(resolvedCost.promptCostUsd || 0) || 0,
        completionCostUsd: Number(resolvedCost.completionCostUsd || 0) || 0,
        totalCostUsd: Number(resolvedCost.totalCostUsd || 0) || 0
      },
      costUsd: Number(resolvedCost.totalCostUsd || 0) || 0,
      errorCode: String(errorCode || '').trim() || null
    });

    if (!(logAttemptsEnabled && requestLogAudit.status === 'pending')) {
      return;
    }

    try {
      await apiInsertChatRequestAttempt({
        requestHash: chatTurn.requestId,
        attemptHash: attemptKey,
        chatDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
        activeProjectId,
        billingOwnerUserId,
        initiatorUserId,
        attemptIndex,
        stageName,
        callKind,
        providerName: 'openrouter',
        providerRequestId,
        candidateModel: modelId,
        responseModel: resolvedModelId,
        transport: 'single',
        status: normalizedStatus,
        isTimeout: normalizedStatus === 'timeout',
        durationMs,
        usage: normalizedUsage,
        cost: resolvedCost,
        errorCode,
        errorMessage,
        requestExcerpt: null,
        responseExcerpt: null,
        metadata: {
          requestId: chatTurn.requestId,
          stageName,
          callKind
        }
      });
    } catch (attemptError) {
      debug('generateAssistantResponse', { type: 'warn' }, attemptError?.message || 'chat-request attempt log failed');
    }
  };

  requestLogAudit = await executePendingRequestLogStage({
    enabled: canPersistRequestLogs,
    currentAudit: requestLogAudit,
    insertChatRequest: apiInsertChatRequest,
    chatDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
    requestId: chatTurn.requestId,
    activeProjectId,
    billingOwnerUserId,
    initiatorUserId,
    chatMode: chatTurn.mode,
    intentKey: chatTurn.intent?.key || 'ask',
    requestedModel: chatTurn.model || null,
    runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
    routingMode: chatTurn.runtime?.routingMode || 'hybrid',
    legacyRequest: Boolean(usedLegacyRequest),
    attemptsEnabled: Boolean(logAttemptsEnabled),
    payloadsEnabled: Boolean(logPayloadsEnabled),
    payloadSampleRate,
    attemptsIndexEnabled: Boolean(attemptsIndexEnabled),
    createPayload: createPendingRequestLogPayload,
    createAuditRecord: createRequestLogAuditRecord,
    onWarn: (message) => {
      debug('generateAssistantResponse', { type: 'warn' }, message);
    }
  });

  let goalStatePersistence = {
    enabled: canPersistGoalState,
    hydrate: {
      status: canPersistGoalState ? 'pending' : 'skipped',
      hit: false,
      updatedAt: null,
      error: null
    },
    persist: {
      status: canPersistGoalState ? 'pending' : 'skipped',
      updatedAt: null,
      error: null
    }
  };

  const hydrateFlow = await executeHydrateAndEmitStage({
    enabled: canPersistGoalState,
    chatDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
    goalState: chatTurn.goalState,
    goalStateHydrated: chatTurn.goalStateHydrated,
    emitStage,
    normalizeGoalState,
    loadChatContext: apiLoadChatContext
  });
  chatTurn.goalState = hydrateFlow?.goalState || chatTurn.goalState;
  chatTurn.goalStateHydrated = Boolean(hydrateFlow?.goalStateHydrated);
  goalStatePersistence = {
    ...goalStatePersistence,
    hydrate: hydrateFlow?.hydrate || goalStatePersistence.hydrate
  };

  const fastRephraseNoNewInfo = Boolean(chatTurn?.contextPlan?.fastRephraseNoNewInfo);
  const intentContractEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_ENABLED !== false;
  const shouldBypassIntentModel = fastRephraseNoNewInfo;

  if (fastRephraseNoNewInfo && !chatTurn?.contextPlan?.explicitResponseModelRequested) {
    const cheapWriteModel = getDefaultModelForTask('chat.write', 'cheap')
      || getDefaultModelForTask('chat.explore', 'cheap')
      || null;
    if (cheapWriteModel) {
      chatTurn.model = cheapWriteModel;
    }
  }

  const classifyIntentFn = shouldBypassIntentModel
    ? async ({ heuristicIntent }) => ({
      ...(heuristicIntent && typeof heuristicIntent === 'object' ? heuristicIntent : {}),
      source: 'heuristic'
    })
    : (intentContractEnabled
      ? classifyChatIntentAndContractWithModelFallback
      : classifyChatIntentWithModelFallback);

  const intentFlow = await executeIntentAndGoalUpdateFlow({
    chatTurn,
    userText,
    signal,
    canPersistGoalState,
    emitStage,
    intentContext: await buildIntentContextBundle({
      chatTurn,
      goalState: chatTurn.goalState,
      activeDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
      projectId: Number(chatTurn.contextPlan?.projectId || 0),
      linkedDocIds: Array.isArray(chatTurn.contextPlan?.linkedDocIds) ? chatTurn.contextPlan.linkedDocIds : [],
      includeProjectSummary: CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_INCLUDE_PROJECT_SUMMARY !== false,
      includeLinkedDocSummaries: CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_INCLUDE_LINKED_DOC_SUMMARIES !== false,
      maxLinkedDocSummaries: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_MAX_LINKED_DOC_SUMMARIES || 6)
    }),
    intentContractConfig: {
      enabled: CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_ENABLED !== false,
      alwaysOn: Boolean(CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_ALWAYS_ON),
      forceOnLongPrompts: CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_FORCE_ON_LONG_PROMPTS !== false,
      longPromptMinSearchableKeywords: Number(
        CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_LONG_PROMPT_MIN_SEARCHABLE_KEYWORDS || 4
      ),
      maxPrimaryKeywords: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_MAX_PRIMARY_KEYWORDS || 8),
      maxSecondaryKeywords: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_MAX_SECONDARY_KEYWORDS || 12),
      maxDocHints: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_MAX_DOC_HINTS || 8)
    },
    onIntentModelAttempt: (attempt) => {
      void logAuxModelAttempt(attempt);
    },
    classifyIntent: classifyIntentFn,
    mergeGoalState,
    summarizeGoalState: summarizeGoalStateForAudit
  });
  chatTurn.intent = intentFlow?.intent || chatTurn.intent;
  chatTurn.goalState = intentFlow?.goalState || chatTurn.goalState;
  chatTurn.contextPlan = carryForwardLinkedDocsFromGoalState({
    contextPlan: chatTurn.contextPlan,
    goalState: chatTurn.goalState,
    userText
  });

  const { queryPlan: plannedQueryPlan } = executePlanAndEmitStage({
    userText,
    intent: chatTurn.intent,
    contextPlan: chatTurn.contextPlan,
    emitStage,
    buildQueryPlan: buildHybridQueryPlan
  });
  chatTurn.queryPlan = plannedQueryPlan;

  const maxCheapCallsPerTurn = Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.MAX_CHEAP_CALLS_PER_TURN || 2));
  const maxTotalCallsPerTurn = Math.max(2, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.MAX_TOTAL_CALLS_PER_TURN || 3));
  let cheapModelCallCount = 0;
  const canRunCheapModelCall = () => {
    const withinCheapBudget = cheapModelCallCount < maxCheapCallsPerTurn;
    // Reserve one call for final response generation.
    const withinTotalBudget = (cheapModelCallCount + 1) < maxTotalCallsPerTurn;
    return withinCheapBudget && withinTotalBudget;
  };

  let routerContract = null;
  emitStage('router', {
    status: 'skipped',
    decisionCode: 'router_removed',
    reasonCodes: ['router_disabled_runtime'],
    confidenceOverall: 0,
    needsReranker: false
  });

  const sufficiencyEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.SUFFICIENCY_ENABLED !== false;
  const refineEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.REFINE_ENABLED !== false;
  const configuredMaxRefineLoops = Number(CONFIG.AI?.CHAT_ORCHESTRATION?.SUFFICIENCY_MAX_REFINE_LOOPS);
  const maxRefineLoops = Number.isFinite(configuredMaxRefineLoops)
    ? Math.max(0, configuredMaxRefineLoops)
    : 1;
  const retrievalFlow = await executeRetrieveSufficiencyFlow({
    runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
    legacyFallback: Boolean(chatTurn.runtime?.legacyFallback),
    docJson,
    userText,
    queryPlan: chatTurn.queryPlan,
    contextPlan: chatTurn.contextPlan,
    routerContract,
    intent: chatTurn.intent,
    signal,
    emitStage,
    onWarn: (message) => {
      debug('generateAssistantResponse', { type: 'warn' }, message);
    },
    retrievePipeline: buildContextRetrievalPipeline,
    evaluateSufficiency: evaluateContextSufficiency,
    buildRefinePlan: buildRefineQueryPlan,
    summarizeContextReferences,
    summarizeContextItems: summarizeContextItemsForAudit,
    buildKeywordCoverage: buildContextKeywordCoverage,
    sufficiencyConfig: {
      minItems: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.SUFFICIENCY_MIN_ITEMS || CONTEXT_SUFFICIENCY_MIN_ITEMS),
      minChars: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.SUFFICIENCY_MIN_CHARS || CONTEXT_SUFFICIENCY_MIN_CHARS),
      minCoverageRatio: Number(
        CONFIG.AI?.CHAT_ORCHESTRATION?.SUFFICIENCY_MIN_COVERAGE_RATIO || CONTEXT_SUFFICIENCY_MIN_COVERAGE_RATIO
      ),
      minMatchedKeywords: Number(
        CONFIG.AI?.CHAT_ORCHESTRATION?.SUFFICIENCY_MIN_MATCHED_KEYWORDS || CONTEXT_SUFFICIENCY_MIN_MATCHED_KEYWORDS
      )
    },
    sufficiencyEnabled,
    refineEnabled,
    maxRefineLoops
  });
  chatTurn.queryPlan = retrievalFlow?.queryPlan || chatTurn.queryPlan;
  chatTurn.routerContract = routerContract;
  let contextRetrieval = retrievalFlow?.contextRetrieval || null;
  let contextSufficiency = retrievalFlow?.contextSufficiency || {
    isSufficient: false,
    decisionCode: 'insufficient',
    reasonCodes: [],
    score: 0,
    selectedCount: 0,
    selectedChars: 0,
    keywordCoverage: null,
    thresholds: null
  };
  let retrievalIterationCount = Number(retrievalFlow?.retrievalIterationCount || 1);

  const rerankerEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.RERANKER_ENABLED === true;
  const rerankerGate = shouldRunReranker({
    routerContract,
    sufficiency: contextSufficiency,
    retrievalMeta: contextRetrieval?.meta,
    confidenceThreshold: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.ROUTER_CONFIDENCE_THRESHOLD || 0.72),
    topDocShareThreshold: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.RERANKER_TOP_DOC_SHARE_THRESHOLD || 0.65)
  });
  if (
    rerankerEnabled
    && rerankerGate.run
    && canRunCheapModelCall()
    && Array.isArray(contextRetrieval?.items)
    && contextRetrieval.items.length > 1
  ) {
    const rerankerStage = await executeRerankerStage({
      enabled: true,
      userText,
      routerContract,
      items: contextRetrieval.items,
      timeoutMs: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.RERANKER_TIMEOUT_MS || 2500),
      signal,
      emitStage,
      onModelAttempt: (attempt) => {
        void logAuxModelAttempt(attempt);
      },
      model: String(CONFIG.AI?.CHAT_ORCHESTRATION?.RERANKER_MODEL || '').trim() || null
    });
    cheapModelCallCount += 1;
    contextRetrieval = {
      ...(contextRetrieval || {}),
      items: Array.isArray(rerankerStage?.items) ? rerankerStage.items : (Array.isArray(contextRetrieval?.items) ? contextRetrieval.items : []),
      meta: {
        ...(contextRetrieval?.meta && typeof contextRetrieval.meta === 'object' ? contextRetrieval.meta : {}),
        rerankerApplied: rerankerStage?.decisionCode === 'reranker_completed',
        rerankerReasonCodes: Array.isArray(rerankerStage?.reasonCodes) ? rerankerStage.reasonCodes : rerankerGate.reasonCodes,
        rerankerConfidence: Number(rerankerStage?.confidence || 0)
      }
    };
  } else if (rerankerEnabled && rerankerGate.run) {
    emitStage('reranker', {
      status: 'skipped',
      decisionCode: 'reranker_budget_exhausted',
      reasonCodes: ['cheap_call_budget_exhausted'],
      confidenceOverall: 0,
      needsReranker: false
    });
  }

  const contextPlanProjectId = Number(chatTurn.contextPlan?.projectId || 0);

  contextRetrieval = await executeConsolidationStage({
    contextRetrieval,
    userText,
    signal,
    emitStage,
    onModelAttempt: (attempt) => {
      void logAuxModelAttempt(attempt);
    },
    consolidationConfig: {
      enabled: CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_ENABLED === true,
      minItems: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_MIN_CONTEXT_ITEMS || 12),
      minChars: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_MIN_CONTEXT_CHARS || 9000),
      maxInputItems: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_MAX_INPUT_ITEMS || 20),
      maxInputChars: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_MAX_INPUT_CHARS || 18000),
      outputMaxChars: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_OUTPUT_MAX_CHARS || 2600),
      keepTopItems: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_KEEP_TOP_ITEMS || 2),
      summaryHarvestMaxPerTurn: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_SUMMARY_MAX_PER_TURN || 6),
      summaryHarvestMinScore: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_SUMMARY_MIN_SCORE || 0.75),
      model: String(CONFIG.AI?.CHAT_ORCHESTRATION?.CONSOLIDATION_MODEL || '').trim() || null
    },
    executeConsolidateStageFn: executeConsolidateStage,
    normalizeContextText,
    extractResponseText: extractAssistantTextFromOpenRouter,
    summarizeSourceBreakdown,
    summarizeDocSelectionBreakdown
  });

  let contextInjectionItems = [];
  if (canPersistGoalState) {
    try {
      const activeDocCtx = await apiLoadDocContext({ docId: Number(chatTurn.contextPlan?.activeDocId || 0) });
      const summaryText = normalizeContextText(activeDocCtx?.docSummaryMd || '');
      if (summaryText) {
        contextInjectionItems.push({
          source: 'docSummary',
          text: summaryText,
          baseScore: 1.45,
          recencyBoost: 0,
          meta: {
            docId: Number(chatTurn.contextPlan?.activeDocId || 0),
            trigger: 'active_doc_summary'
          }
        });
      }
    } catch (err) {
      debug('generateAssistantResponse', { type: 'warn' }, `doc summary load failed: ${err?.message || 'unknown'}`);
    }
  }

  if (contextPlanProjectId > 0) {
    try {
      const projectCtx = await apiLoadProjectContext({ projectId: contextPlanProjectId });
      const projectSummaryText = normalizeContextText(projectCtx?.summaryMd || '');
      if (projectSummaryText) {
        contextInjectionItems.push({
          source: 'projectContext',
          text: projectSummaryText,
          baseScore: 1.5,
          recencyBoost: 0,
          meta: {
            projectId: contextPlanProjectId,
            trigger: 'project_context_summary'
          }
        });
      }
    } catch (err) {
      debug('generateAssistantResponse', { type: 'warn' }, `project context load failed: ${err?.message || 'unknown'}`);
    }
  }

  const selectStage = executeSelectAndEmitStage({
    docJson,
    userText,
    systemPrompt,
    maxHistoryMessages,
    runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
    intentKey: chatTurn.intent?.key || 'ask',
    chatMode: chatTurn.mode,
    formatPolicy: chatTurn.formatPolicy,
    contextItems: [
      ...contextInjectionItems,
      ...(Array.isArray(contextRetrieval?.items) ? contextRetrieval.items : [])
    ],
    selectionContexts: Array.isArray(chatTurn?.contextPlan?.selectionContexts)
      ? chatTurn.contextPlan.selectionContexts
      : [],
    contextChars: Number(contextRetrieval?.meta?.selectedChars || 0),
    contextReferences: summarizeContextReferences(contextRetrieval?.items, 4),
    usedFallbackRequest: usedLegacyRequest,
    fallbackEnabled: Boolean(chatTurn.runtime?.legacyFallback),
    goalState: chatTurn.goalState,
    buildFallbackMessages,
    emitStage,
    onTraceWarn: (payload) => {
      debug('chat.ai.trace', { type: 'warn' }, {
        requestId: chatTurn.requestId,
        ...payload
      });
    }
  });
  let messages = Array.isArray(selectStage?.messages) ? selectStage.messages : [];
  let typedPayloadAudit = selectStage?.typedPayloadAudit || null;
  let typedContextSummary = selectStage?.typedContextSummary || {
    requiredBlocks: [],
    optionalBlocks: [],
    optionalBlockCounts: {},
    conflictResolutionEvents: [],
    conflictResolutionReasonCodes: []
  };
  usedLegacyRequest = Boolean(selectStage?.usedLegacyRequest);

  if (!messages.length) {
    return {
      text: 'Thinking...',
      raw: null
    };
  }

  const candidates = createRequestCandidates({
    model,
    fallbackModels,
    unavailableModelIds
  });
  const blockedModelSet = buildBlockedModelSet();

  let requestStageEmitted = false;
  const executeRequest = async (requestMessages) => {
    const mergeRequestExecutionState = (requestResult) => {
      requestStageEmitted = Boolean(requestResult?.requestStageEmitted);
      requestAttemptCounter = Number(requestResult?.requestAttemptCounter || requestAttemptCounter);
      if (Array.isArray(requestResult?.requestAttemptsAudit) && requestResult.requestAttemptsAudit.length > 0) {
        requestAttemptsAudit.push(...requestResult.requestAttemptsAudit);
      }
    };

    try {
      const requestResult = await executeRequestWithCandidateRetries({
        requestMessages,
        candidates,
        stream,
        signal,
        temperature,
        maxTokens,
        usedLegacyRequest,
        routingMode: chatTurn.runtime?.routingMode || 'hybrid',
        requestId: chatTurn.requestId,
        onStreamText,
        normalizeStreamText: formatAiResponseText,
        emitStage,
        requestStageEmitted,
        shouldCapturePayloads,
        maxExcerptChars,
        clipText: clipAuditText,
        calculateUsageCost: calculateUsageCostUsd,
        extractUsage: extractUsageMetrics,
        extractResponseText: extractAssistantTextFromOpenRouter,
        callKind: 'response',
        chatDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
        activeProjectId,
        billingOwnerUserId,
        initiatorUserId,
        logAttemptsEnabled,
        requestLogReady: requestLogAudit.status === 'pending',
        insertRequestAttempt: apiInsertChatRequestAttempt,
        requestAttemptCounterStart: requestAttemptCounter,
        blockedModelSet,
        normalizeResponseModelId,
        isBlockedResponseModel,
        unavailableModelIds,
        isRateLimitedStatus: (error) => Number(error?.status || 0) === 429,
        isTransientRateLimitError,
        isModelUnavailableError,
        isGuardrailRestrictionError,
        shouldRetryRequestError,
        setRateLimitUntil: (nextMs) => {
          openRouterRateLimitUntilMs = Number(nextMs || 0);
        },
        rateLimitCooldownMs: OPENROUTER_RATE_LIMIT_COOLDOWN_MS,
        buildRateLimitError,
        maxTransientRetriesPerCandidate: MAX_TRANSIENT_RETRIES_PER_CANDIDATE,
        baseTransientRetryDelayMs: BASE_TRANSIENT_RETRY_DELAY_MS,
        sleep,
        onTraceWarn: (payload) => {
          debug('chat.ai.trace', { type: 'warn' }, payload);
        },
        onWarn: (message) => {
          debug('generateAssistantResponse', { type: 'warn' }, message);
        },
        debugLabel: 'request',
        executeRequestStageFn: executeRequestStage
      });

      mergeRequestExecutionState(requestResult);

      return {
        response: requestResult?.response || null,
        responseText: String(requestResult?.responseText || '')
      };
    } catch (error) {
      mergeRequestExecutionState(error?.requestExecutionState);
      if (!requestStageEmitted) {
        requestStageEmitted = true;
      }
      throw error;
    }
  };

  let requestFlow = null;
  try {
    requestFlow = await executeRequestWithFallbackMessages({
      messages,
      executeRequest,
      runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
      fallbackEnabled: Boolean(chatTurn.runtime?.legacyFallback),
      requestId: chatTurn.requestId,
      buildFallbackMessages,
      docJson,
      userText,
      systemPrompt,
      maxHistoryMessages,
      onTraceWarn: (payload) => {
        debug('chat.ai.trace', { type: 'warn' }, payload);
      }
    });
  } catch (error) {
    const {
      rollupUsage,
      rollupCostUsd,
      rollupDurationMs,
      rollupFailedAttempts,
      rollupTimeoutAttempts,
      attemptsSummary,
      attemptsIndex
    } = createRequestRollupArtifacts({ attempts: requestAttemptsAudit });
    const failureStatus = /timed out|timeout/i.test(String(error?.message || ''))
      || String(error?.code || '') === 'AI_TIMEOUT'
      || String(error?.code || '') === 'AI_REQUEST_ABORTED'
      ? 'timeout'
      : 'failed';

    requestLogAudit = await executeRequestLogPersistStage({
      enabled: canPersistRequestLogs,
      currentAudit: requestLogAudit,
      insertChatRequest: apiInsertChatRequest,
      chatDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
      requestId: chatTurn.requestId,
      providerRequestId: null,
      activeProjectId,
      billingOwnerUserId,
      initiatorUserId,
      chatMode: chatTurn.mode,
      intentKey: chatTurn.intent?.key || 'ask',
      requestedModel: chatTurn.model || null,
      responseModel: null,
      attemptCount: requestAttemptsAudit.length,
      failedAttemptCount: rollupFailedAttempts,
      timeoutAttemptCount: rollupTimeoutAttempts,
      totalDurationMs: rollupDurationMs,
      attemptsIndex,
      attemptSummary: attemptsSummary,
      attemptsIndexEnabled,
      rollupUsage,
      rollupCostUsd,
      runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
      routingMode: chatTurn.runtime?.routingMode || 'hybrid',
      legacyRequest: Boolean(usedLegacyRequest),
      finalResponseModel: null,
      finalResponseTokens: 0,
      finalResponseCostUsd: 0,
      finalResponsePromptCostUsd: 0,
      finalResponseCompletionCostUsd: 0,
      status: failureStatus,
      errorMessage: String(error?.message || '').trim() || 'request failed',
      createAuditRecord: createRequestLogAuditRecord,
      onWarn: (message) => {
        debug('generateAssistantResponse', { type: 'warn' }, message);
      }
    });
    error.requestKey = chatTurn.requestId;
    throw error;
  }
  let response = requestFlow?.response || null;
  let responseText = requestFlow?.responseText || '';
  usedLegacyRequest = Boolean(usedLegacyRequest || requestFlow?.usedFallbackRequest || requestFlow?.usedLegacyRequest);

  const rawText = stream
    ? formatAiResponseText(responseText)
    : formatAiResponseText(extractAssistantTextFromOpenRouter(response));

  const contextItemsForGrounding = Array.isArray(contextRetrieval?.items) ? contextRetrieval.items : [];
  const shouldEnforceGrounding = Boolean(chatTurn.runtime.enabled)
    && String(chatTurn.mode || '').trim().toLowerCase() === 'explore';
  const evidenceAnchors = shouldEnforceGrounding
    ? collectGroundingEvidenceAnchors(contextItemsForGrounding)
    : [];
  const groundingResult = await executeGroundingStage({
    enabled: shouldEnforceGrounding,
    rawText,
    stream,
    messages,
    evidenceAnchors,
    response,
    responseText,
    executeRequest,
    normalizeResponseText: formatAiResponseText,
    extractResponseText: extractAssistantTextFromOpenRouter,
    validateGroundingAnchors,
    buildGroundingRetryMessage,
    buildGroundingUncertaintyText
  });
  response = groundingResult?.response || response;
  responseText = groundingResult?.responseText || responseText;
  const groundedRawText = groundingResult?.groundedRawText || rawText;
  const groundingStatus = groundingResult?.groundingStatus || (shouldEnforceGrounding ? 'grounding-not-required' : 'grounding-skipped');
  const groundingRetryUsed = Boolean(groundingResult?.groundingRetryUsed);
  const groundingReasonCodes = Array.isArray(groundingResult?.groundingReasonCodes)
    ? groundingResult.groundingReasonCodes
    : [];
  const groundingUngroundedEntities = Array.isArray(groundingResult?.groundingUngroundedEntities)
    ? groundingResult.groundingUngroundedEntities
    : [];

  const finalizeStageResult = chatTurn.runtime.enabled
    ? executeFinalizeStage({
      responseText: groundedRawText,
      intentKey: chatTurn.intent?.key,
      formatPolicy: chatTurn.formatPolicy
    })
    : { text: groundedRawText };
  const text = finalizeStageResult?.text || groundedRawText;

  const usage = extractUsageMetrics(response);
  const responseModel = String(response?.model || model || '').trim() || null;
  const cost = calculateUsageCostUsd({ modelId: responseModel, usage });
  const {
    rollupUsage,
    rollupCostUsd,
    rollupDurationMs,
    rollupFailedAttempts,
    rollupTimeoutAttempts,
    attemptsSummary,
    attemptsIndex
  } = createRequestRollupArtifacts({ attempts: requestAttemptsAudit });
  const providerRequestId = String(response?.id || '').trim() || null;
  requestLogAudit = await executeRequestLogPersistStage({
    enabled: canPersistRequestLogs,
    currentAudit: requestLogAudit,
    insertChatRequest: apiInsertChatRequest,
    chatDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
    requestId: chatTurn.requestId,
    providerRequestId,
    activeProjectId,
    billingOwnerUserId,
    initiatorUserId,
    chatMode: chatTurn.mode,
    intentKey: chatTurn.intent?.key || 'ask',
    requestedModel: chatTurn.model || null,
    responseModel,
    attemptCount: requestAttemptsAudit.length,
    failedAttemptCount: rollupFailedAttempts,
    timeoutAttemptCount: rollupTimeoutAttempts,
    totalDurationMs: rollupDurationMs,
    attemptsIndex,
    attemptSummary: attemptsSummary,
    attemptsIndexEnabled,
    rollupUsage,
    rollupCostUsd,
    runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
    routingMode: chatTurn.runtime?.routingMode || 'hybrid',
    legacyRequest: Boolean(usedLegacyRequest),
    finalResponseModel: responseModel,
    finalResponseTokens: Number(usage?.totalTokens || 0) || 0,
    finalResponseCostUsd: Number(cost?.totalCostUsd || 0) || 0,
    finalResponsePromptCostUsd: Number(cost?.promptCostUsd || 0) || 0,
    finalResponseCompletionCostUsd: Number(cost?.completionCostUsd || 0) || 0,
    status: 'success',
    errorMessage: null,
    createAuditRecord: createRequestLogAuditRecord,
    onWarn: (message) => {
      debug('generateAssistantResponse', { type: 'warn' }, message);
    }
  });

  emitStage('finalize', createFinalizeStagePayload({
    finalizeResult: finalizeStageResult?.finalizeResult || null,
    text,
    runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
    usedLegacyRequest,
    responseModel,
    groundingStatus,
    groundingRetryUsed,
    groundingReasonCodes,
    groundingUngroundedEntities,
    usage,
    cost
  }));

  const maxSummarizedPerTurn = Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.SUMMARY_MAX_PER_TURN || 16));
  const retrievalSummaryHashes = Array.isArray(contextRetrieval?.meta?.summaryBackfillNeededChunkHashes)
    ? contextRetrieval.meta.summaryBackfillNeededChunkHashes.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  const consolidationSummaryHashes = Array.isArray(contextRetrieval?.meta?.consolidationSummaryCandidateChunkHashes)
    ? contextRetrieval.meta.consolidationSummaryCandidateChunkHashes.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  const mergedSummaryHashes = Array.from(new Set([
    ...retrievalSummaryHashes,
    ...consolidationSummaryHashes
  ])).slice(0, maxSummarizedPerTurn);
  const summarizeCandidateHashes = await buildSummaryCandidateChunkHashes({
    baseChunkHashes: mergedSummaryHashes,
    maxPerTurn: maxSummarizedPerTurn
  });

  let summarizeStageResult = {
    status: 'skipped',
    decisionCode: 'summarize_no_candidates',
    reasonCodes: ['no_summary_candidates'],
    candidateChunkCount: 0,
    summarizedChunkCount: 0,
    summarizedDocCount: 0,
    summarizedChunkHashes: [],
    summarizedDocIds: [],
    errors: []
  };

  if (summarizeCandidateHashes.length > 0) {
    try {
      const summarizeRetrieval = {
        ...(contextRetrieval || {}),
        meta: {
          ...(contextRetrieval?.meta && typeof contextRetrieval.meta === 'object' ? contextRetrieval.meta : {}),
          summaryBackfillNeededChunkHashes: summarizeCandidateHashes
        }
      };
      summarizeStageResult = await runSummarizeStage({
        contextRetrieval: summarizeRetrieval,
        userText,
        signal,
        persistWrites: true,
        onModelAttempt: (attempt) => {
          void logAuxModelAttempt(attempt);
        }
      });
      try {
        await upsertProjectSummaryFromSummarizeStage({
          projectId: contextPlanProjectId,
          summarizeStage: summarizeStageResult
        });
      } catch (err) {
        debug('generateAssistantResponse', { type: 'warn' }, `project summary upsert failed: ${err?.message || 'unknown'}`);
      }
    } catch (err) {
      debug('generateAssistantResponse', { type: 'warn' }, `summarize stage failed: ${err?.message || 'unknown'}`);
      summarizeStageResult = {
        status: 'failed',
        decisionCode: 'summarize_failed',
        reasonCodes: ['summarize_error'],
        candidateChunkCount: summarizeCandidateHashes.length,
        summarizedChunkCount: 0,
        summarizedDocCount: 0,
        summarizedChunkHashes: [],
        summarizedDocIds: [],
        errors: [{ message: String(err?.message || 'summarize stage failed') }]
      };
    }
  }

  emitStage('summarize', {
    status: summarizeStageResult?.status || 'skipped',
    decisionCode: summarizeStageResult?.decisionCode || 'summarize_unknown',
    reasonCodes: Array.isArray(summarizeStageResult?.reasonCodes) ? summarizeStageResult.reasonCodes : [],
    candidateChunkCount: Number(summarizeStageResult?.candidateChunkCount || 0),
    summarizedChunkCount: Number(summarizeStageResult?.summarizedChunkCount || 0),
    summarizedDocCount: Number(summarizeStageResult?.summarizedDocCount || 0),
    summarizedChunkHashes: Array.isArray(summarizeStageResult?.summarizedChunkHashes)
      ? summarizeStageResult.summarizedChunkHashes
      : [],
    summarizedDocIds: Array.isArray(summarizeStageResult?.summarizedDocIds)
      ? summarizeStageResult.summarizedDocIds
      : [],
    summarizeErrors: Array.isArray(summarizeStageResult?.errors) ? summarizeStageResult.errors : []
  });

  const persistResult = await executePersistAndEmitStage({
    enabled: canPersistGoalState,
    chatDocId: Number(chatTurn.contextPlan?.activeDocId || 0),
    requestId: chatTurn.requestId,
    goalState: chatTurn.goalState && typeof chatTurn.goalState === 'object' ? chatTurn.goalState : {},
    requestLogAudit,
    goalStatePersistence,
    summarizeGoalState: summarizeGoalStateForAudit,
    emitStage,
    onWarn: (message) => {
      debug('generateAssistantResponse', { type: 'warn' }, message);
    },
    upsertChatContext: apiUpsertChatContext
  });
  const persistStageResult = persistResult?.persistStageResult || null;
  goalStatePersistence = persistResult?.goalStatePersistence || {
    ...goalStatePersistence,
    persist: createPersistStateRecord({ persistResult: persistStageResult })
  };

  return {
    text: text || 'I could not generate a response yet.',
    raw: response,
    requestKey: chatTurn.requestId,
    trace: createAssistantTrace({
      requestId: chatTurn.requestId,
      mode: chatTurn.mode,
      runtimeEnabled: Boolean(chatTurn.runtime?.enabled),
      routingMode: chatTurn.runtime?.routingMode || 'hybrid',
      usage,
      cost,
      userText,
      clipAuditText,
      intent: chatTurn.intent,
      goalState: chatTurn.goalState,
      summarizeGoalState: summarizeGoalStateForAudit,
      goalStatePersistence,
      requestLogAudit,
      queryPlan: chatTurn.queryPlan,
      retrievalIterationCount,
      contextSufficiency,
      contextRetrieval,
      summarizeContextItems: summarizeContextItemsForAudit,
      buildKeywordCoverage: buildContextKeywordCoverage,
      typedContextSummary,
      typedPayloadAudit,
      stageTrace
    })
  };
};
